import { Box, Divider, Grid } from '@mui/material'
import CustomText from 'components/common/CustomText/CustomText'
import CustomDatePicker from 'components/inputs/CustomDatePicker/CustomDatePicker'
import CustomFormSelect from 'components/inputs/CustomFormSelect/CustomFormSelect'
import CustomFormTextInput from 'components/inputs/CustomFormTextInput/CustomFormTextInput'
import { useMemo } from 'react'
import { FieldError, FieldErrorsImpl, Merge, useFormContext, useWatch } from 'react-hook-form'
import { colors } from 'styles/colors'
import { caseSharedProps, procedureDataType } from 'types/types'
import CustomFormMultiSelect from 'components/inputs/CustomFormMultiSelect/CustomFormMultiSelect'
import { isAbscess, isAneurysm, isAVF, isAVM, isBedside, isBleeding, isCranial, isDissection, isFracture, isFunctional, isHematoma, isHydrocephalus, isInter, isInterventional, isNeurosurgery, isPain, isPeripheral, isRadiology, isRadiosurgery, isSpine, isStroke, isTumor, isVasospasm, showAccess, showApproach, showBoneGraft, showComplications, showDecompressionLevels, showDurationOfProcedure, showDurationOfRadiation, showExtensionToPelvis, showFindings, showFusion, showFusionLevels, showImplant, showImplantType, showInterbodyFusion, showInterLocation, showListIndication, showMinimallyInvasive, showMorphogenicProtein, showNavigation, showNerve, showNeuroLocation, showOsteotomy, showOtherVendors, showOutcome, showProcedure, showRobotic, showSide, showTarget, showTextIndication, showTextLocation, showTiciGrade, showVascularClosureDevice, showVendors } from 'common/caseHelpers'
import useGlobalStyles from 'styles/globalStyles'

export type RHFMaybeFieldError =
  | FieldError
  | Merge<FieldError, FieldErrorsImpl<any>>
  | undefined;

export const toFieldError = (err: unknown): FieldError | undefined => {
  if (!err || typeof err !== 'object') return undefined;

  if ('type' in err && typeof (err as any).type === 'string') {
    return err as FieldError;
  }

  return undefined;
};


interface Props {
  shared: caseSharedProps;
}

export default function ProcedureDetails({
  shared,
}: Props) {
  const globalStyles = useGlobalStyles();

  const {
    procedureTypes,
    cranialApproachList,
    spineApproachList,
    interventionalIndicationList,
    radiologyIndicationList,
    cranialIndicationList,
    spineIndicationList,
    functionalIndicationList,
    nervesIndicationList,
    interIndicationList,
    speciality = '',
    spineLocationList,
    strokeLocationList,
    interventionalProcedureTypeList,
    aneurysmLocationList,
    hematomaLocationList,
    fractureLocationList,
    hydrocephalusLocationList,
    avmLocationList,
    bleedingLocationList,
    aneurysmLocationInterList,
    dissectionLocationList,
    tumorLocationList,
    bedsideProcedureTypeList,
    yesNoList = [],
    isMultipleSelection,
    sideList,
    ticiGradeList,
    osteotomyList,
    vendorsList,
    positionList,
  } = shared;

  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<procedureDataType>()

  const values = useWatch();
  const procedureType = useWatch({ name: 'type' })
  const indication = useWatch({ name: 'indication' });

  const approachList = useMemo(() => {
    return isCranial(procedureType) ? cranialApproachList
      : isSpine(procedureType) ? spineApproachList
        : [];
  }, [procedureType]);

  const indicationList = useMemo(() => {
    return isInterventional(speciality) ? interventionalIndicationList
      : isRadiology(speciality) ? radiologyIndicationList
        : isCranial(procedureType) ? cranialIndicationList
          : isSpine(procedureType) ? spineIndicationList
            : isFunctional(procedureType) ? functionalIndicationList
              : isPeripheral(procedureType) ? nervesIndicationList
                : interIndicationList
  }, [procedureType]);

  const procedureList = useMemo(() => {
    return (isInterventional(speciality) ||
      isNeurosurgery(speciality) && isInter(procedureType)) ? interventionalProcedureTypeList
      : isNeurosurgery(speciality) && (isBedside(procedureType) || isPain(procedureType) || isRadiosurgery(procedureType)) ? bedsideProcedureTypeList
        : [];
  }, [procedureType, speciality]);

  const neuroLocationList = useMemo(() => {
    return isCranial(procedureType) && (isTumor(indication) || isAVM(indication) || isAVF(indication) || isAbscess(indication)) ? tumorLocationList
      : isCranial(procedureType) && isAneurysm(indication) ? aneurysmLocationList
        : isCranial(procedureType) && isHematoma(indication) ? hematomaLocationList
          : isCranial(procedureType) && isFracture(indication) ? fractureLocationList
            : isCranial(procedureType) && isHydrocephalus(indication) ? hydrocephalusLocationList
              : isSpine(procedureType) ? spineLocationList
                : isInter(procedureType) && (isAVM(indication) || isAVF(indication)) ? avmLocationList
                  : isInter(procedureType) && isBleeding(indication) ? bleedingLocationList
                    : isInter(procedureType) && isAneurysm(indication) ? aneurysmLocationInterList
                      : isStroke(indication) ? strokeLocationList
                        : isInter(procedureType) && (isDissection(indication) || isVasospasm(indication) || isStroke(indication)) ? dissectionLocationList
                          : []
  }, [procedureType, indication])

  const interLocationList = useMemo(() => {
    return (isAVM(indication) || isAVF(indication)) ? avmLocationList
      : isBleeding(indication) ? bleedingLocationList
        : isAneurysm(indication) ? aneurysmLocationInterList
          : isStroke(indication) ? strokeLocationList
            : (isDissection(indication) || isVasospasm(indication) || isStroke(indication)) ? dissectionLocationList
              : []
  }, [procedureType, indication]);


  return (
    <Box
      className={globalStyles.sectionContainer}
    >
      <Box className={globalStyles.sectionHeader}>
        <CustomText
          text="Procedure Details"
          color={colors.primary}
          fontWeight={600}
        />
      </Box>

      <Divider />

      <Grid container p={3} spacing={1.5}>

        {/* Date of procedure */}
        <Grid size={{ xs: 12, md: 6 }}>
          <CustomDatePicker
            name="date_of_procedure"
            label="Date of Procedure"
            required={true}
            control={control}
            placeholder="Date of procedure"
          />
        </Grid>

        {/* Procedure name */}
        <Grid size={{ xs: 12, md: 6 }}>
          <CustomFormTextInput
            name="procedure_name"
            label="Procedure Name"
            required={true}
            placeholder="Procedure name"
            register={register}
            error={toFieldError(errors.procedure_name)}
          />
        </Grid>


        {/* Procedure type */}
        <Grid size={{ xs: 12, md: 6 }}>
          {procedureTypes?.length ? (
            <CustomFormSelect
              name="type"
              label="Procedure Type"
              required={true}
              placeholder="Procedure type"
              error={toFieldError(errors.type)}
              options={procedureTypes || []}
            />
          ) : (
            <CustomFormTextInput
              name="type"
              label="Procedure Type"
              required={true}
              placeholder="Procedure type"
              register={register}
              error={toFieldError(errors.type)}
            />
          )}
        </Grid>


        {/* Approach */}
        {showApproach(values) && (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormMultiSelect
              name="approach"
              label="Approach"
              required={false}
              placeholder="Select approach"
              control={control}
              error={toFieldError(errors.approach)}
              options={approachList || []}
            />
          </Grid>
        )}

        {/* Position */}
        <Grid size={{ xs: 12, md: 6 }}>
          <CustomFormMultiSelect
            name="position"
            label="Position"
            required={false}
            placeholder="Select position"
            control={control}
            error={toFieldError(errors.position)}
            options={positionList || []}
          />
        </Grid>

        {/* Minimally invasive */}
        {showMinimallyInvasive(values, speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormSelect
              name="minimally_invasive"
              label="Minimally Invasive"
              required={false}
              placeholder="Minimally Invasive"
              error={toFieldError(errors.minimally_invasive)}
              options={yesNoList || []}
            />
          </Grid>
        ) : null}

        {/* Navigation */}
        {showNavigation(values, speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormSelect
              name="navigation"
              label="Navigation"
              required={false}
              placeholder="Navigation"
              error={toFieldError(errors.navigation)}
              options={yesNoList || []}
            />
          </Grid>
        ) : null}

        {/* Robotic */}
        {showRobotic(values, speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormSelect
              name="robotic"
              label="Robotic"
              required={false}
              placeholder="Robotic"
              error={toFieldError(errors.robotic)}
              options={yesNoList || []}
            />
          </Grid>
        ) : null}

        {/* Indication */}
        {showTextIndication(values, speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormTextInput
              name="indication"
              label="Indication"
              required={false}
              placeholder="Indication"
              register={register}
              error={toFieldError(errors.indication)}
            />
          </Grid>
        ) : showListIndication(values, speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            {isMultipleSelection ? (
              <CustomFormMultiSelect
                name="indication"
                label="Indication"
                required={false}
                placeholder="Select indication"
                control={control}
                error={toFieldError(errors.indication)}
                options={indicationList || []}
              />
            ) : (
              <CustomFormSelect
                name="indication"
                label="Indication"
                required={false}
                placeholder="Indication"
                error={toFieldError(errors.indication)}
                options={indicationList || []}
              />
            )}
          </Grid>
        ) : null}

        {/* Side */}
        {showSide(speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormSelect
              name="side"
              label="Side"
              required={false}
              placeholder="Side"
              error={toFieldError(errors.side)}
              options={sideList || []}
            />
          </Grid>
        ) : null}

        {/* Target */}
        {showTarget(values, speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormTextInput
              name="target"
              label="Target"
              required={false}
              placeholder="Target"
              register={register}
              error={toFieldError(errors.target)}
            />
          </Grid>
        ) : null}

        {/* Nerve */}
        {showNerve(values, speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormTextInput
              name="nerve"
              label="Nerve"
              required={false}
              placeholder="Nerve"
              register={register}
              error={toFieldError(errors.nerve)}
            />
          </Grid>
        ) : null}

        {/* Text Location */}
        {showTextLocation(values, speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormTextInput
              name="location"
              label="Location"
              required={false}
              placeholder="Location"
              register={register}
              error={toFieldError(errors.location)}
            />
          </Grid>
        ) : null}

        {/* Neuro Location */}
        {showNeuroLocation(values, speciality) && neuroLocationList?.length ? (
          <Grid size={{ xs: 12, md: 6 }}>
            {isMultipleSelection ? (
              <CustomFormMultiSelect
                name="location"
                label="Location"
                required={false}
                placeholder="Select location"
                control={control}
                options={neuroLocationList || []}
                error={toFieldError(errors.location)}
              />
            ) : (
              <CustomFormSelect
                name="location"
                label="Location"
                required={false}
                placeholder="Select location"
                options={neuroLocationList || []}
                error={toFieldError(errors.location)}
              />
            )}
          </Grid>
        ) : null}

        {/* Inter Location */}
        {showInterLocation(values, speciality) && interLocationList?.length ? (
          <Grid size={{ xs: 12, md: 6 }}>
            {isMultipleSelection ? (
              <CustomFormMultiSelect
                name="location"
                label="Location"
                required={false}
                placeholder="Select location"
                control={control}
                options={interLocationList || []}
                error={toFieldError(errors.location)}
              />
            ) : (
              <CustomFormSelect
                name="location"
                label="Location"
                required={false}
                placeholder="Select location"
                options={interLocationList || []}
                error={toFieldError(errors.location)}
              />
            )}
          </Grid>
        ) : null}

        {/* Procedure */}
        {showProcedure(values, speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            {isMultipleSelection ? (
              <CustomFormMultiSelect
                name="_procedure"
                label="Procedure"
                required={false}
                placeholder="Select procedure"
                control={control}
                options={procedureList || []}
                error={toFieldError(errors._procedure)}
              />
            ) : (
              <CustomFormSelect
                name="_procedure"
                label="Procedure"
                required={false}
                placeholder="Select procedure"
                options={procedureList || []}
                error={toFieldError(errors._procedure)}
              />
            )}
          </Grid>
        ) : null}

        {/* Tici grade */}
        {showTiciGrade(values, speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormSelect
              name="tici_grade"
              label="TICI Grade"
              required={false}
              placeholder="Select TICI grade"
              options={ticiGradeList || []}
              error={toFieldError(errors.tici_grade)}
            />
          </Grid>
        ) : null}

        {/* Osteotomy */}
        {showOsteotomy(values, speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormSelect
              name="osteotomy"
              label="Osteotomy"
              required={false}
              placeholder="Select osteotomy"
              options={osteotomyList || []}
              error={toFieldError(errors.osteotomy)}
            />
          </Grid>
        ) : null}

        {/* Fusion */}
        {showFusion(values, speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormSelect
              name="_fusion"
              label="Fusion"
              required={false}
              placeholder="Select option"
              options={yesNoList}
              error={toFieldError(errors._fusion)}
            />
          </Grid>
        ) : null}

        {/* Interbody fusion */}
        {showInterbodyFusion(values, speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormSelect
              name="interbody_fusion"
              label="Interbody Fusion"
              required={false}
              placeholder="Select option"
              options={yesNoList}
              error={toFieldError(errors.interbody_fusion)}
            />
          </Grid>
        ) : null}

        {/* Extension to pelvis */}
        {showExtensionToPelvis(values, speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormSelect
              name="extension_to_pelvis"
              label="Extension to Pelvis"
              required={false}
              placeholder="Select option"
              options={yesNoList}
              error={toFieldError(errors.extension_to_pelvis)}
            />
          </Grid>
        ) : null}

        {/* Fusion levels */}
        {showFusionLevels(values, speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormTextInput
              name="fusion_levels"
              label="Fusion Levels"
              required={false}
              placeholder="Fusion Levels"
              register={register}
              error={toFieldError(errors.fusion_levels)}
            />
          </Grid>
        ) : null}

        {/* Decompression levels */}
        {showDecompressionLevels(values, speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormTextInput
              name="decompression_levels"
              label="Decompression Levels"
              required={false}
              placeholder="Decompression Levels"
              register={register}
              error={toFieldError(errors.decompression_levels)}
            />
          </Grid>
        ) : null}

        {/* Morphogenic protein */}
        {showMorphogenicProtein(values, speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormSelect
              name="morphogenic_protein"
              label="Morphogenic Protein"
              required={false}
              placeholder="Select option"
              options={yesNoList}
              error={toFieldError(errors.morphogenic_protein)}
            />
          </Grid>
        ) : null}

        {/* Bone Graft */}
        {showBoneGraft(values, speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormSelect
              name="bone_graft"
              label="Bone Graft"
              required={false}
              placeholder="Select option"
              options={yesNoList}
              error={toFieldError(errors.bone_graft)}
            />
          </Grid>
        ) : null}

        {/* Implant */}
        {showImplant(values, speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormSelect
              name="implant"
              label="Implant"
              required={false}
              placeholder="Select option"
              options={yesNoList}
              error={toFieldError(errors.implant)}
            />
          </Grid>
        ) : null}

        {/* Implant type */}
        {showImplantType(values, speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormTextInput
              name="implant_type"
              label="Implant Type"
              required={false}
              placeholder="Implant Type"
              register={register}
              error={toFieldError(errors.implant_type)}
            />
          </Grid>
        ) : null}

        {/* Vendors */}
        <Grid size={{ xs: 12, md: 6 }}>
          <CustomFormMultiSelect
            name="_vendors"
            label="Vendors"
            required={false}
            placeholder="Select vendors"
            control={control}
            options={vendorsList || []}
            error={toFieldError(errors._vendors)}
          />
        </Grid>

        {/* Other vendors */}
        {showOtherVendors(values) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormTextInput
              name="vendors"
              label="Other Vendors"
              required={false}
              placeholder="Other vendors"
              register={register}
              error={toFieldError(errors.vendors)}
            />
          </Grid>
        ) : null}

        {/* Access */}
        {showAccess(speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormTextInput
              name="access"
              label="Access"
              required={false}
              placeholder="Access"
              register={register}
              error={toFieldError(errors.access)}
            />
          </Grid>
        ) : null}

        {/* Vascular closure device */}
        {showVascularClosureDevice(speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormTextInput
              name="vascular_closure_device"
              label="Vascular Closure Device"
              required={false}
              placeholder="Vascular closure device (if used)"
              register={register}
              error={toFieldError(errors.vascular_closure_device)}
            />
          </Grid>
        ) : null}

        {/* Duration of Radiation */}
        {showDurationOfRadiation(values, speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormTextInput
              name="duration_of_radiation"
              label="Duration of Radiation"
              required={false}
              placeholder="Duration of Radiation"
              register={register}
              error={toFieldError(errors.duration_of_radiation)}
            />
          </Grid>
        ) : null}

        {/* Duration of Procedure */}
        {showDurationOfProcedure(speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormTextInput
              name="duration_of_procedure"
              label="Duration of Procedure"
              required={false}
              placeholder="Duration of Procedure"
              register={register}
              error={toFieldError(errors.duration_of_procedure)}
            />
          </Grid>
        ) : null}

        {/* Findings */}
        {showFindings(speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormTextInput
              name="findings"
              label="Findings"
              required={false}
              placeholder="Findings"
              register={register}
              error={toFieldError(errors.findings)}
            />
          </Grid>
        ) : null}

        {/* Complications */}
        {showComplications(speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormTextInput
              name="complications"
              label="Complications"
              required={false}
              placeholder="Complications"
              register={register}
              error={toFieldError(errors.complications)}
            />
          </Grid>
        ) : null}

        {/* Outcome */}
        {showOutcome(speciality) ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormTextInput
              name="outcome"
              label="Outcome"
              required={false}
              placeholder="Outcome"
              register={register}
              error={toFieldError(errors.outcome)}
            />
          </Grid>
        ) : null}

      </Grid>

    </Box>
  )
}
